(function () {
  'use strict';

  /**
   * Appstle Subscriptions widget tweaks:
   *  1. Rename the pay-as-you-go option label ("Subscribe & Save") to
   *     "Monthly" so the editorial product page reads as a 3-plan stack.
   *  2. Expand the Prepaid dropdown into one card per selling plan
   *     (Annual / 6 months) by cloning the inner <option>s as additional
   *     `appstle_subscription_wrapper_option` cards. Clicks on the cloned
   *     cards forward their value to the original <select> and fire a
   *     change event so Appstle's own state updates.
   *
   * The widget renders asynchronously, so we use a MutationObserver on
   * <body> and re-run on every DOM mutation until the widget is found.
   *
   * Pure parsing helpers live in `appstle-tweak-helpers.js` and are
   * loaded *before* this file via two `<script defer>` tags. Both files
   * being defer-loaded preserves their order and guarantees the helpers
   * are present when this IIFE evaluates.
   */

  var helpers = window.GG_AppstleHelpers || {};
  var extractPerDeliveryPrice = helpers.extractPerDeliveryPrice;
  var extractDiscountLabel = helpers.extractDiscountLabel;
  var extractPlanName = helpers.extractPlanName;
  var calcDiscountedPrice = helpers.calcDiscountedPrice;
  var normalizePlanName = helpers.normalizePlanName;
  var injectHelperText = helpers.injectHelperText;
  var MONTHLY_LABEL = 'Monthly Plan';
  var TWEAKED_FLAG = 'ggAppstleTweaked';

  function renamePayAsYouGoLabel(widget) {
    var payAsYouGo = widget.querySelector('.payAsYouGoPlansDropdownWrapper');
    if (!payAsYouGo) return;
    var label = payAsYouGo.querySelector('.appstle_subscribe_save_text');
    if (label && label.textContent.trim() !== MONTHLY_LABEL) {
      label.textContent = MONTHLY_LABEL;
    }
    injectHelperText(payAsYouGo, MONTHLY_LABEL);
  }

  /**
   * Read the per-product plan ID overrides written by the product section
   * via `<script>window.GG_PRODUCT_PLANS = { ... }</script>`. Returns the
   * set of allowed selling_plan_id strings, or null if no override was
   * provided (in which case the widget is shown untouched).
   */
  function getAllowedPlanIds() {
    var override = window.GG_PRODUCT_PLANS;
    if (!override) return null;
    var allowed = [];
    ['monthly', 'sixMonth', 'annual'].forEach(function (key) {
      if (override[key]) allowed.push(String(override[key]).trim());
    });
    if (allowed.length === 0) return null;
    return allowed;
  }

  function filterPlansByMetafield(widget) {
    var allowed = getAllowedPlanIds();
    if (!allowed) return;

    // Filter top-level option cards (Monthly, Prepaid wrapper, clones)
    widget.querySelectorAll('.appstle_subscription_wrapper_option').forEach(function (opt) {
      var radio = opt.querySelector('input[type="radio"]');
      var planValue = opt.dataset.planValue || (radio ? radio.value : '');

      var nestedSelect = opt.querySelector('select');
      if (nestedSelect) {
        // Prepaid wrapper has a <select>; trim disallowed <option>s
        Array.from(nestedSelect.querySelectorAll('option')).forEach(function (o) {
          if (allowed.indexOf(o.value) === -1) o.remove();
        });
        if (nestedSelect.querySelectorAll('option').length === 0) {
          opt.style.display = 'none';
        }
        return;
      }

      if (planValue && allowed.indexOf(planValue) === -1) {
        opt.style.display = 'none';
      }
    });
  }

  function buildClonedCardHtml(opt, planName, discount, price, monthlyPrice) {
    var showCompare = monthlyPrice && monthlyPrice !== price;
    // We use bespoke `gg-clone-*` classes for the price elements so
    // Appstle's own widget JS can't overwrite them with the variant base
    // price. CSS in appstle-overrides.css mirrors the appstle_* equivalents.
    return (
      '<div class="appstle_subscription_radio_wrapper">' +
      '<input type="radio" name="selling_plan_clone" value="' +
      opt.value +
      '">' +
      '<label class="appstle_radio_label">' +
      '<span class="appstle_circle"><span class="appstle_dot"></span></span>' +
      '<span class="appstle-text-and-badge-wrapper">' +
      '<span class="appstle_subscribe_save_text">' +
      planName +
      '</span>' +
      (discount ? '<span class="appstle-save-badge">' + discount + '</span>' : '') +
      '</span>' +
      '</label>' +
      '<div class="appstle_subscription_amount_wrapper">' +
      (showCompare ? '<span class="gg-clone-compare">' + monthlyPrice + '</span>' : '') +
      (price
        ? '<span class="gg-clone-amount">' +
          price +
          '<span class="gg-clone-suffix">/delivery</span>' +
          '</span>'
        : '') +
      '</div>' +
      '</div>'
    );
  }

  function expandPrepaidPlans(widget) {
    var prepaidWrapper = widget.querySelector('.prepaidPlansDropdownWrapper');
    if (!prepaidWrapper) return;
    var select = prepaidWrapper.querySelector('select');
    if (!select) return;
    var options = Array.from(select.querySelectorAll('option'));
    if (options.length <= 1) return;

    var parent = prepaidWrapper.parentElement;
    if (!parent) return;
    var origRadio = prepaidWrapper.querySelector('input[type="radio"][name="selling_plan"]');

    // hide the original prepaid card; we replace it with one card per option
    prepaidWrapper.style.display = 'none';

    // pick up the monthly (pay-as-you-go) per-delivery price; we use it
    // as the base for discount math AND as the strikethrough compare
    // amount on each prepaid clone.
    var monthlyPrice = '';
    var monthlyAmountEl = widget.querySelector(
      '.payAsYouGoPlansDropdownWrapper .appstle_subscription_amount'
    );
    if (monthlyAmountEl) {
      monthlyPrice = monthlyAmountEl.textContent.trim();
    }

    // wait until the monthly base price is rendered. Without it we'd
    // either skip discounting entirely or compute against zero.
    if (!/\$[\d,]+/.test(monthlyPrice)) return;

    // already expanded? skip to avoid infinite mutation observer loops
    if (parent.querySelector('[data-gg-prepaid-clone]')) {
      return;
    }

    // We render Monthly → 6 months → Annual: appending Annual *first* and
    // then 6 months as `afterend` of prepaidWrapper inverts the natural
    // <option> order so the longest commitment ends up at the bottom.
    var orderedOptions = options.slice().reverse();
    var insertAfter = prepaidWrapper;
    orderedOptions.forEach(function (opt) {
      var optionText = opt.textContent.trim();
      var planName = normalizePlanName(extractPlanName(optionText));
      var discount = extractDiscountLabel(optionText);
      // Compute per-delivery price ourselves: monthly × (1 − discount%).
      // Falls back to whatever price we can scrape from the option if the
      // computation fails (e.g. discount label missing).
      var price = calcDiscountedPrice(monthlyPrice, discount);
      if (!price) {
        price = extractPerDeliveryPrice(opt);
      }

      var card = document.createElement('div');
      card.dataset.ggPrepaidClone = '1';
      card.dataset.planValue = opt.value;
      card.className = 'appstle_subscription_wrapper_option appstle_selected_background';
      card.innerHTML = buildClonedCardHtml(opt, planName, discount, price, monthlyPrice);
      injectHelperText(card, planName);

      card.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        // sync original <select> + fire change so Appstle updates state
        if (select.value !== opt.value) {
          select.value = opt.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // ensure the original prepaid radio is checked so Appstle treats
        // prepaid as the active wrapper
        if (origRadio && !origRadio.checked) {
          origRadio.checked = true;
          origRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        markActive(widget, card);
      });

      insertAfter.insertAdjacentElement('afterend', card);
      insertAfter = card;
    });

    // keep the active highlight in sync with the original selection
    var initialValue = select.value;
    parent.querySelectorAll('[data-gg-prepaid-clone]').forEach(function (clone) {
      if (clone.dataset.planValue === initialValue && origRadio && origRadio.checked) {
        markActive(widget, clone);
      }
    });
  }

  function markActive(widget, activeCard) {
    widget.querySelectorAll('.appstle_subscription_wrapper_option').forEach(function (opt) {
      opt.classList.remove('appstle-active-option');
    });
    activeCard.classList.add('appstle-active-option');
    var radio = activeCard.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;
  }

  function logPlanIds(widget) {
    var plans = [];
    widget.querySelectorAll('.appstle_subscription_wrapper_option').forEach(function (opt) {
      var label = opt.querySelector('.appstle_subscribe_save_text');
      var radio = opt.querySelector('input[type="radio"]');
      var planValue = opt.dataset.planValue || (radio ? radio.value : '');
      var planLabel = label ? label.textContent.trim() : '(no label)';
      if (planValue) {
        plans.push({ name: planLabel, id: planValue });
      }

      // also collect prepaid <select> options if not yet expanded
      var nestedSelect = opt.querySelector('select');
      if (nestedSelect) {
        Array.from(nestedSelect.querySelectorAll('option')).forEach(function (o) {
          plans.push({ name: o.textContent.trim(), id: o.value });
        });
      }
    });

    var productIdInput = widget.closest('form')
      ? widget.closest('form').querySelector('input[name="id"]')
      : null;
    var productId = productIdInput ? productIdInput.value : '(no product id)';

    console.log('[GG Appstle] product_variant=' + productId, plans);

    // Render a fixed debug bar at the bottom of the page so the IDs are
    // visible without opening DevTools. Toggle by appending ?gg-debug=1
    // to the URL (or remove this block when no longer needed).
    if (/\bgg-debug=1\b/.test(window.location.search)) {
      var bar = document.getElementById('gg-appstle-debug');
      if (!bar) {
        bar = document.createElement('div');
        bar.id = 'gg-appstle-debug';
        bar.style.cssText =
          'position:fixed;bottom:0;left:0;right:0;background:#0f1a13;color:#c9a96e;padding:8px 16px;font:11px/1.4 monospace;z-index:9999;border-top:1px solid #c9a96e;white-space:pre-wrap;';
        document.body.appendChild(bar);
      }
      bar.textContent =
        '[GG Appstle] variant=' +
        productId +
        '\n' +
        plans
          .map(function (p) {
            return '  ' + p.name + ' → ' + p.id;
          })
          .join('\n');
    }
  }

  function tweak(widget) {
    if (!widget) return;
    renamePayAsYouGoLabel(widget);
    expandPrepaidPlans(widget);
    filterPlansByMetafield(widget);
    logPlanIds(widget);
    widget.dataset[TWEAKED_FLAG] = '1';
  }

  var observer;

  function findAndTweak() {
    if (observer) observer.disconnect();
    document.querySelectorAll('.appstle_sub_widget').forEach(tweak);
    if (observer) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  observer = new MutationObserver(function () {
    findAndTweak();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', findAndTweak);
  } else {
    findAndTweak();
  }
  observer.observe(document.body, { childList: true, subtree: true });
})();
